import React, { useState } from 'react';
import { Livraison, Recuperation } from '../types';
import { livraisonAPI, recuperationAPI, signalementAPI } from '../services/api';
import { speak, buildMissionText, MESSAGE_VOIX_ABSENTE } from '../utils/tts';
import { vibrate, vibrateTap, vibrateError } from '../utils/haptic';
import { classerErreur, ErreurMetier } from '../utils/erreursReseau';
import {destinationMission, libelleDestination} from '../utils/destinationMission';

interface Props {
    mission: Livraison | Recuperation;
    type: 'livraison' | 'recuperation';
    prenom: string;
    onBack: () => void;
    onSuccess: () => void;
}

type Step = 'ATTENTE' | 'ACCEPTER' | 'EN_ROUTE' | 'ARRIVE' | 'LIVRE';

// Stepper simplifié : 2 étapes seulement
const STEPS_LIVR: { key: Step; label: string; icon: string; color: string }[] = [
    { key: 'ACCEPTER', label: "J'accepte", icon: '✅', color: '#3b82f6' },
    { key: 'LIVRE',    label: 'Livré !',   icon: '🎉', color: '#22c55e' },
];

const MissionDetailScreen: React.FC<Props> = ({ mission, type, prenom, onBack, onSuccess }) => {
    const isLivr    = type === 'livraison';
    const data      = mission as any;
    const conteneur = data.conteneur || data.numeroBL || '—';
    const client    = data.clientDestination || data.client || '—';
    // Sur un depot parc, la destination du jour est le PARC : cf. utils/destinationMission.
    const lieu      = destinationMission(data);
    const libelleLieu = libelleDestination(data);
    const currentStatut: Step = (data.statutWorkflow || 'ATTENTE') as Step;

    const [loading, setLoading]   = useState(false);
    const [error,   setError]     = useState('');
    const [speaking, setSpeaking] = useState(false);
    const [gpsWarning, setGpsWarning] = useState('');
    const [signalementEnCours, setSignalementEnCours] = useState(false);
    const [signalementEtat, setSignalementEtat] = useState<{ ok: boolean; texte: string } | null>(null);

    // Position rapide (max 5s), ne bloque jamais l'action même si elle échoue — mais on garde
    // une trace visible (gpsWarning) au lieu de l'échec silencieux d'origine : ce même écran
    // envoyait "MISSION TERMINÉE" sans aucune position, ce qui rendait le conteneur invisible
    // sur la carte "TC à récupérer" sans qu'aucune erreur ne soit jamais montrée à l'utilisateur.
    const getQuickPosition = (
        sansPosition = 'conteneur livré sans position'
    ): Promise<{ latitude: number; longitude: number } | undefined> => {
        return new Promise((resolve) => {
            if (!navigator.geolocation) {
                setGpsWarning('Géolocalisation non disponible sur cet appareil');
                resolve(undefined);
                return;
            }
            const timer = setTimeout(() => {
                setGpsWarning(`Position GPS non obtenue à temps — ${sansPosition}`);
                resolve(undefined);
            }, 5000);
            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    clearTimeout(timer);
                    setGpsWarning('');
                    resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude });
                },
                () => {
                    clearTimeout(timer);
                    setGpsWarning(`Position GPS refusée ou indisponible — ${sansPosition}`);
                    resolve(undefined);
                },
                { enableHighAccuracy: true, timeout: 5000, maximumAge: 60000 }
            );
        });
    };

    const [voixAbsente, setVoixAbsente] = useState(false);

    const handleSpeak = () => {
        vibrateTap();
        const text = buildMissionText({ prenom, conteneur, client, lieu, type });

        // Pas de voix française sur l'appareil : le dire, plutôt que d'allumer un
        // indicateur « lecture en cours » devant un silence de cinq secondes.
        if (!speak(text)) {
            setVoixAbsente(true);
            return;
        }
        setSpeaking(true);
        setTimeout(() => setSpeaking(false), 5000);
    };

    // Mapper les statuts intermédiaires vers les 2 étapes simplifiées
    const stepKey: Step = ['EN_ROUTE','ARRIVE','EN_COURS'].includes(currentStatut)
        ? 'ACCEPTER'
        : (['LIVRE','TERMINEE','RECUPEREE'].includes(currentStatut) ? 'LIVRE' : currentStatut as Step);
    const currentIdx = STEPS_LIVR.findIndex(s => s.key === stepKey);

    // Flux simplifié : 2 étapes seulement
    // ATTENTE → J'ACCEPTE → MISSION TERMINÉE
    // `terminal` distingue l'action qui achève la mission de celle qui la fait simplement avancer.
    // C'est ce drapeau — et non le statut affiché — qui décide de l'écran de confirmation.
    type Action = {
        label: string; icon: string; color: string;
        terminal: boolean;
        fn: () => Promise<any>;
    };

    const getNextAction = (): Action | null => {
        const id = data.id;
        if (isLivr) {
            if (currentStatut === 'ATTENTE')
                return { label: "J'ACCEPTE LA MISSION", icon: '✅', color: '#3b82f6', terminal: false, fn: () => livraisonAPI.accepter(id) };
            if (['ACCEPTER', 'EN_ROUTE', 'ARRIVE'].includes(currentStatut))
                return {
                    label: "MISSION TERMINÉE ✅", icon: '🎉', color: '#22c55e', terminal: true,
                    fn: async () => {
                        const position = await getQuickPosition();
                        return livraisonAPI.livrer(id, { latitude: position?.latitude, longitude: position?.longitude });
                    }
                };
        } else {
            if (currentStatut === 'ATTENTE')
                return { label: "J'ACCEPTE LA MISSION", icon: '✅', color: '#3b82f6', terminal: false, fn: () => recuperationAPI.accepter(id) };
            if (['ACCEPTER', 'EN_ROUTE', 'ARRIVE', 'EN_COURS'].includes(currentStatut))
                return { label: "RÉCUPÉRATION TERMINÉE ✅", icon: '🎉', color: '#22c55e', terminal: true, fn: () => recuperationAPI.recuperer(id, {}) };
        }
        return null;
    };

    const nextAction = getNextAction();

    /**
     * Le backend peut répondre 200 en signalant lui-même un échec métier (`success: false`).
     * Le traiter comme une réussite afficherait « MISSION TERMINÉE » sur une mission restée
     * ouverte côté serveur.
     */
    const verifierReponse = (reponse: any) => {
        if (reponse && reponse.success === false) {
            throw new ErreurMetier(reponse.message || 'Action refusée par le serveur.');
        }
    };

    const handleAction = async () => {
        if (!nextAction || loading) return;
        vibrate(60);
        setLoading(true);
        setError('');
        try {
            const reponse = await nextAction.fn();
            verifierReponse(reponse);

            // L'écran de confirmation ne s'ouvre qu'après un aller-retour serveur réussi sur
            // l'action terminale. Auparavant il dépendait du statut déjà affiché — une mission
            // terminée depuis ACCEPTER rechargeait la page au lieu de confirmer, et une mission
            // au statut ARRIVE confirmait même si le serveur avait refusé.
            if (nextAction.terminal) {
                onSuccess();
            } else {
                window.location.reload();
            }
        } catch (e: any) {
            vibrateError();
            setError(classerErreur(e).message);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Signalement d'un problème.
     *
     * Ce bouton annonçait vocalement « L'équipe va être prévenue » sans qu'aucun appel ne parte :
     * le chauffeur repartait convaincu d'avoir alerté le back-office. Le succès n'est désormais
     * annoncé qu'après confirmation du serveur, et l'échec est dit explicitement.
     */
    const handleProblem = async () => {
        if (signalementEnCours) return; // garde contre le double appui
        vibrateTap();
        setSignalementEnCours(true);
        setSignalementEtat(null);
        try {
            const position = await getQuickPosition('signalement envoyé sans position');
            const reponse = await signalementAPI.signaler({
                missionId: data.id,
                typeMission: isLivr ? 'LIVRAISON' : 'RECUPERATION',
                latitude: position?.latitude,
                longitude: position?.longitude,
            });
            verifierReponse(reponse);

            const dejaEnvoye = reponse?.data?.doublonIgnore === true;
            const texte = dejaEnvoye
                ? 'Ce problème a déjà été signalé. Le back-office est prévenu.'
                : 'Problème signalé. Le back-office est prévenu.';
            setSignalementEtat({ ok: true, texte });
            speak(texte);
        } catch (e: any) {
            vibrateError();
            const texte = classerErreur(e).message;
            setSignalementEtat({
                ok: false,
                texte: `Signalement NON envoyé. ${texte}`,
            });
            speak("Échec de l'envoi. Le back-office n'a pas été prévenu. Réessayez.");
        } finally {
            setSignalementEnCours(false);
        }
    };

    return (
        <div style={styles.screen}>
            {/* Header */}
            <div style={styles.header}>
                <div style={styles.headerTop}>
                    <button onClick={onBack} style={styles.backBtn}>← RETOUR</button>
                    <span style={{ ...styles.typeBadge, background: isLivr ? '#f59e0b' : '#0369a1' }}>
                        {isLivr ? '📦 LIVRAISON' : '♻️ RÉCUPÉRATION'}
                    </span>
                </div>
            </div>

            <div style={styles.content}>
                {/* Conteneur — hero */}
                <div style={styles.heroBox}>
                    <p style={styles.heroLabel}>CONTENEUR</p>
                    <p style={styles.heroNum}>{conteneur}</p>
                </div>

                {/* Infos client/lieu */}
                <div style={styles.infoCard}>
                    <div style={styles.infoRow}>
                        <span style={styles.infoIcon}>👤</span>
                        <div>
                            <p style={styles.infoLabel}>CLIENT</p>
                            <p style={styles.infoValue}>{client}</p>
                        </div>
                    </div>
                    <div style={styles.divider} />
                    <div style={styles.infoRow}>
                        <span style={styles.infoIcon}>📍</span>
                        <div>
                            <p style={styles.infoLabel}>{libelleLieu}</p>
                            <p style={styles.infoValue}>{lieu}</p>
                        </div>
                    </div>
                    {data.dateRendezVous && (
                        <>
                            <div style={styles.divider} />
                            <div style={styles.infoRow}>
                                <span style={styles.infoIcon}>🕐</span>
                                <div>
                                    <p style={styles.infoLabel}>RENDEZ-VOUS</p>
                                    <p style={styles.infoValue}>{new Date(data.dateRendezVous).toLocaleString('fr-FR')}</p>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* Stepper progression */}
                <div style={styles.stepperBox}>
                    {STEPS_LIVR.map((step, i) => {
                        const done    = i <= currentIdx;
                        const current = i === currentIdx;
                        return (
                            <React.Fragment key={step.key}>
                                <div style={styles.stepItem}>
                                    <div style={{
                                        ...styles.stepCircle,
                                        background:   done ? step.color : '#1e293b',
                                        borderColor:  done ? step.color : '#334155',
                                        transform:    current ? 'scale(1.2)' : 'scale(1)',
                                    }}>
                                        <span style={{ fontSize: current ? 18 : 14 }}>{step.icon}</span>
                                    </div>
                                    <p style={{ ...styles.stepLabel, color: done ? '#f8fafc' : '#475569', fontWeight: current ? 800 : 400 }}>
                                        {step.label}
                                    </p>
                                </div>
                                {i < STEPS_LIVR.length - 1 && (
                                    <div style={{ ...styles.stepLine, background: i < currentIdx ? '#22c55e' : '#1e293b' }} />
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>

                {/* Bouton ÉCOUTER */}
                <button onClick={handleSpeak} style={{ ...styles.voiceBtn, background: speaking ? '#6d28d9' : '#7c3aed' }}>
                    <span style={{ fontSize: 24 }}>{speaking ? '📢' : '🔊'}</span>
                    <span style={styles.voiceBtnText}>{speaking ? 'LECTURE EN COURS...' : 'ÉCOUTER LA MISSION'}</span>
                </button>
                {voixAbsente && (
                    <p style={{ margin: '10px 0 0', padding: '10px 12px', borderRadius: 10,
                                background: '#fef3c7', color: '#92400e', fontSize: 14,
                                lineHeight: 1.45 }}>
                        {MESSAGE_VOIX_ABSENTE}
                    </p>
                )}

                {/* Erreur */}
                {error && (
                    <div role="alert" style={styles.errorBox}>
                        <span style={{ fontSize: 20 }}>⚠️</span>
                        <span style={styles.errorText}>{error}</span>
                    </div>
                )}

                {/* Avertissement GPS — visible plutôt que silencieux comme avant ce fix */}
                {gpsWarning && (
                    <div style={styles.errorBox}>
                        <span style={{ fontSize: 20 }}>📍</span>
                        <span style={styles.errorText}>{gpsWarning}</span>
                    </div>
                )}

                {/* Bouton action principale */}
                {nextAction && (
                    <button
                        onClick={handleAction}
                        disabled={loading}
                        style={{ ...styles.actionBtn, background: loading ? '#374151' : nextAction.color, opacity: loading ? 0.7 : 1 }}>
                        {loading ? (
                            <span style={styles.actionBtnText}>⏳ Envoi en cours...</span>
                        ) : (
                            <span style={styles.actionBtnText}>
                                {nextAction.icon} {nextAction.label}
                            </span>
                        )}
                    </button>
                )}

                {/* Résultat du signalement — jamais un succès tant que le serveur n'a pas confirmé */}
                {signalementEtat && (
                    <div
                        role="status"
                        style={signalementEtat.ok ? styles.successBox : styles.errorBox}>
                        <span style={{ fontSize: 20 }}>{signalementEtat.ok ? '✅' : '⚠️'}</span>
                        <span style={signalementEtat.ok ? styles.successText : styles.errorText}>
                            {signalementEtat.texte}
                        </span>
                    </div>
                )}

                {/* Bouton problème */}
                <button
                    onClick={handleProblem}
                    disabled={signalementEnCours}
                    aria-label="Signaler un problème"
                    style={{ ...styles.problemBtn, opacity: signalementEnCours ? 0.6 : 1 }}>
                    <span>{signalementEnCours ? '⏳ ENVOI DU SIGNALEMENT...' : '⚠️ SIGNALER UN PROBLÈME'}</span>
                </button>
            </div>
        </div>
    );
};

const styles: Record<string, React.CSSProperties> = {
    screen:      { minHeight: '100vh', background: '#0f172a', display: 'flex', flexDirection: 'column' },
    header:      { background: '#1e293b', paddingTop: 'env(safe-area-inset-top, 16px)', borderBottom: '1px solid #334155' },
    headerTop:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px' },
    backBtn:     { background: 'none', border: 'none', color: '#22c55e', fontSize: 16, fontWeight: 700, cursor: 'pointer', padding: '8px 0' },
    typeBadge:   { color: '#000', fontSize: 11, fontWeight: 800, padding: '5px 12px', borderRadius: 20 },
    content:     { flex: 1, padding: 16, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 14 },
    heroBox:     { background: '#0f172a', borderRadius: 16, padding: '20px', border: '2px solid #f59e0b', textAlign: 'center' },
    heroLabel:   { color: '#64748b', fontSize: 11, fontWeight: 700, letterSpacing: 2, margin: 0, textTransform: 'uppercase' },
    heroNum:     { color: '#f59e0b', fontSize: 28, fontWeight: 900, margin: '8px 0 0', fontFamily: 'monospace', letterSpacing: 3 },
    infoCard:    { background: '#1e293b', borderRadius: 16, padding: '4px 20px', border: '1px solid #334155' },
    infoRow:     { display: 'flex', alignItems: 'center', gap: 16, padding: '16px 0' },
    infoIcon:    { fontSize: 28, flexShrink: 0 },
    infoLabel:   { color: '#64748b', fontSize: 10, fontWeight: 700, letterSpacing: 1, margin: 0, textTransform: 'uppercase' },
    infoValue:   { color: '#f1f5f9', fontSize: 18, fontWeight: 700, margin: '4px 0 0' },
    divider:     { height: 1, background: '#334155', marginLeft: 44 },
    stepperBox:  { display: 'flex', alignItems: 'center', background: '#1e293b', borderRadius: 16, padding: '16px 12px', border: '1px solid #334155' },
    stepItem:    { display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, gap: 6 },
    stepCircle:  { width: 40, height: 40, borderRadius: '50%', border: '2px solid #334155', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.3s' },
    stepLabel:   { fontSize: 10, margin: 0, textAlign: 'center', lineHeight: 1.2 },
    stepLine:    { height: 3, flex: 0.4, borderRadius: 2, transition: 'background 0.3s' },
    voiceBtn:    { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, background: '#7c3aed', border: 'none', borderRadius: 16, padding: '18px', cursor: 'pointer', transition: 'background 0.2s' },
    voiceBtnText: { color: '#fff', fontSize: 16, fontWeight: 800, letterSpacing: 1 },
    errorBox:    { background: '#7f1d1d', borderRadius: 12, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10, border: '1px solid #ef4444' },
    errorText:   { color: '#fca5a5', fontSize: 15, fontWeight: 600 },
    successBox:  { background: '#14532d', borderRadius: 12, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10, border: '1px solid #22c55e' },
    successText: { color: '#86efac', fontSize: 15, fontWeight: 600 },
    actionBtn:   { border: 'none', borderRadius: 18, padding: '22px 20px', cursor: 'pointer', width: '100%', boxShadow: '0 4px 20px rgba(0,0,0,0.4)', transition: 'all 0.2s' },
    actionBtnText: { color: '#000', fontSize: 20, fontWeight: 900, letterSpacing: 1 },
    problemBtn:  { background: 'transparent', border: '2px solid #374151', borderRadius: 14, padding: '16px', color: '#ef4444', fontSize: 15, fontWeight: 700, cursor: 'pointer', width: '100%', marginBottom: 32 },
};

export default MissionDetailScreen;
